/**
 * Arquivo: src/pages/Relatorios.tsx
 * Versão: 5.3.1
 * Data: 27/09/2026
 * Descrição: Faz cada card abrir exatamente os lançamentos que compõem seu valor e refina a PWA.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '../components/Modal'
import { obterBaseAtiva, obterDadosAnaliticos } from '../services/financeiro'
import { usePeriod } from '../context/PeriodContext'
import { cashDate, isSettled, movementValue } from '../utils/financialPresentation'
import type { Categoria, Lancamento } from '../types/database'

const moeda=(v:number)=>v.toLocaleString('pt-BR',{style:'currency',currency:'BRL'})
const realizado=(l:Lancamento)=>isSettled(l.status)
const valor=movementValue
const dataBR=(v?:string|null)=>{if(!v)return '—';const p=String(v).slice(0,10).split('-');return p.length===3?`${p[2]}/${p[1]}/${p[0]}`:String(v)}

type Detalhe = {tipo:'mes'|'categoria'|'entradas'|'saidas'|'resultado';chave?:string}

export default function Relatorios(){
 const navigate=useNavigate()
 const { periodo, label } = usePeriod()
 const [lanc,setLanc]=useState<Lancamento[]>([])
 const [cats,setCats]=useState<Categoria[]>([])
 const [erro,setErro]=useState('')
 const [detalhe,setDetalhe]=useState<Detalhe|null>(null)

 useEffect(()=>{(async()=>{try{
   const b=await obterBaseAtiva();if(!b)return
   const d=await obterDadosAnaliticos(b.id)
   setLanc(d.lancamentos);setCats(d.categorias)
 }catch(e:any){setErro(e.message||'Erro ao carregar relatórios.')}})()},[])

 const lancPeriodo=useMemo(
   ()=>periodo==='ALL'?lanc:lanc.filter(l=>cashDate(l).slice(0,7)===periodo),
   [lanc,periodo]
 )
 const realizadosPeriodo=useMemo(()=>lancPeriodo.filter(realizado),[lancPeriodo])

 // Cards e detalhamento usam exatamente a mesma base de dados.
 const tot=useMemo(()=>realizadosPeriodo.reduce((a,l)=>{
   if(l.tipo==='RECEITA')a.r+=valor(l)
   else if(l.tipo==='DESPESA')a.d+=valor(l)
   return a
 },{r:0,d:0}),[realizadosPeriodo])

 const meses=useMemo(()=>{
   const m=new Map<string,{r:number,d:number}>()
   realizadosPeriodo.forEach(l=>{
     const k=cashDate(l).slice(0,7)
     if(!k)return
     const x=m.get(k)||{r:0,d:0}
     if(l.tipo==='RECEITA')x.r+=valor(l)
     else if(l.tipo==='DESPESA')x.d+=valor(l)
     m.set(k,x)
   })
   return [...m.entries()].sort().slice(-12)
 },[realizadosPeriodo])

 const catMap=useMemo(()=>new Map(cats.map(c=>[c.id,c.nome])),[cats])
 const despesas=useMemo(()=>{
   const m=new Map<string,number>()
   realizadosPeriodo.filter(l=>l.tipo==='DESPESA').forEach(l=>{
     const k=catMap.get(l.categoria_id||'')||'Sem categoria'
     m.set(k,(m.get(k)||0)+valor(l))
   })
   return [...m.entries()].sort((a,b)=>b[1]-a[1]).slice(0,12)
 },[realizadosPeriodo,catMap])
 const maxCategoria=despesas[0]?.[1]||1

 const registrosDetalhe=useMemo(()=>realizadosPeriodo.filter(l=>
   !detalhe
   || detalhe.tipo==='resultado'
   || detalhe.tipo==='mes'&&cashDate(l).slice(0,7)===detalhe.chave
   || detalhe.tipo==='categoria'&&l.tipo==='DESPESA'&&(catMap.get(l.categoria_id||'')||'Sem categoria')===detalhe.chave
   || detalhe.tipo==='entradas'&&l.tipo==='RECEITA'
   || detalhe.tipo==='saidas'&&l.tipo==='DESPESA'
 ),[realizadosPeriodo,detalhe,catMap])

 const resumoDetalhe=useMemo(()=>registrosDetalhe.reduce((a,l)=>{
   if(l.tipo==='RECEITA')a.r+=valor(l)
   else if(l.tipo==='DESPESA')a.d+=valor(l)
   return a
 },{r:0,d:0}),[registrosDetalhe])

 const tituloDetalhe=
   detalhe?.tipo==='mes'?`Movimentos · ${detalhe.chave?.slice(5)}/${detalhe.chave?.slice(0,4)}`
   :detalhe?.tipo==='categoria'?`Saídas · ${detalhe.chave}`
   :detalhe?.tipo==='entradas'?'Entradas realizadas'
   :detalhe?.tipo==='saidas'?'Saídas realizadas'
   :'Resultado do período'

 return <div className="page reports-page">
   <div className="page-heading"><div><h1>Relatórios</h1><p>Valores realizados pela data de pagamento/recebimento • <strong>{label}</strong>.</p></div></div>
   {erro&&<div className="alert error">{erro}</div>}

   <div className="cards-grid cards-3 management-summary reports-summary">
     <button className="metric-card desktop-clickable-card" onClick={()=>setDetalhe({tipo:'entradas'})}><span>Entradas realizadas</span><strong className="privacy-value income">{moeda(tot.r)}</strong></button>
     <button className="metric-card desktop-clickable-card" onClick={()=>setDetalhe({tipo:'saidas'})}><span>Saídas realizadas</span><strong className="privacy-value expense">{moeda(tot.d)}</strong></button>
     <button className="metric-card desktop-clickable-card" onClick={()=>setDetalhe({tipo:'resultado'})}><span>Resultado realizado</span><strong className={`privacy-value ${tot.r-tot.d>=0?'income':'expense'}`}>{moeda(tot.r-tot.d)}</strong></button>
   </div>

   <div className="management-grid">
     <section className="panel desktop-list-panel">
       <div className="panel-header"><h2>Evolução mensal</h2><span>Últimos 12 meses com movimento</span></div>
       <div className="table-wrap"><table className="reports-table"><thead><tr><th>Mês</th><th>Entradas</th><th>Saídas</th><th>Resultado</th></tr></thead><tbody>
         {meses.map(([m,x])=><tr key={m} className="desktop-clickable-row" tabIndex={0} role="button" aria-label={`Ver movimentos de ${m}`} onClick={()=>setDetalhe({tipo:'mes',chave:m})} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setDetalhe({tipo:'mes',chave:m})}}}><td><strong>{m.slice(5)}/{m.slice(0,4)}</strong></td><td className="value-income privacy-value">{moeda(x.r)}</td><td className="value-expense privacy-value">{moeda(x.d)}</td><td className="privacy-value">{moeda(x.r-x.d)}</td></tr>)}
       </tbody></table></div>
     </section>

     <section className="mobile-only-list reports-mobile-panel">
       <div className="mobile-list-heading"><strong>Evolução mensal</strong><span>{meses.length}</span></div>
       <div className="mobile-record-list">
         {meses.map(([m,x])=><button className="mobile-record-item reports-month-item" key={m} onClick={()=>setDetalhe({tipo:'mes',chave:m})}><span className="mobile-record-main"><strong>{m.slice(5)}/{m.slice(0,4)}</strong><small>Entradas e saídas realizadas no mês</small></span><span className="mobile-record-side"><strong className={`privacy-value ${x.r-x.d>=0?'income':'expense'}`}>{moeda(x.r-x.d)}</strong><small>Resultado</small></span></button>)}
         {!meses.length&&<div className="mobile-empty-state">Nenhum movimento no período.</div>}
       </div>
     </section>

     <section className="panel reports-category-panel">
       <div className="panel-header"><h2>Maiores saídas por categoria</h2><span>Valores realizados</span></div>
       <div className="ranking-list reports-ranking-desktop">
         {despesas.map(([n,v],i)=><button className="ranking-row desktop-clickable-card" key={n} onClick={()=>setDetalhe({tipo:'categoria',chave:n})}><span>{i+1}. {n}</span><strong className="privacy-value">{moeda(v)}</strong></button>)}
       </div>
       <div className="reports-ranking-mobile">
         {despesas.map(([n,v],i)=><button key={n} className="reports-category-row" onClick={()=>setDetalhe({tipo:'categoria',chave:n})}>
           <span className="reports-category-rank">{i+1}</span>
           <span className="reports-category-main">
             <span className="reports-category-title"><strong>{n}</strong><b className="privacy-value">{moeda(v)}</b></span>
             <span className="reports-category-track"><i style={{width:`${Math.max(5,(v/maxCategoria)*100)}%`}}/></span>
           </span>
         </button>)}
         {!despesas.length&&<div className="mobile-empty-state">Nenhuma saída realizada no período.</div>}
       </div>
     </section>
   </div>

   <Modal aberto={!!detalhe} titulo={tituloDetalhe} subtitulo={label} largo onClose={()=>setDetalhe(null)}>
     <div className="report-detail-content">
       <div className={`report-detail-summary ${detalhe?.tipo==='resultado'||detalhe?.tipo==='mes'?'three':'single'}`}>
         {detalhe?.tipo==='entradas'&&<div><span>Total de entradas</span><strong className="income privacy-value">{moeda(resumoDetalhe.r)}</strong><small>{registrosDetalhe.length} lançamento(s)</small></div>}
         {(detalhe?.tipo==='saidas'||detalhe?.tipo==='categoria')&&<div><span>Total de saídas</span><strong className="expense privacy-value">{moeda(resumoDetalhe.d)}</strong><small>{registrosDetalhe.length} lançamento(s)</small></div>}
         {(detalhe?.tipo==='resultado'||detalhe?.tipo==='mes')&&<>
           <div><span>Entradas</span><strong className="income privacy-value">{moeda(resumoDetalhe.r)}</strong></div>
           <div><span>Saídas</span><strong className="expense privacy-value">{moeda(resumoDetalhe.d)}</strong></div>
           <div><span>Resultado realizado</span><strong className={`${resumoDetalhe.r-resumoDetalhe.d>=0?'income':'expense'} privacy-value`}>{moeda(resumoDetalhe.r-resumoDetalhe.d)}</strong></div>
         </>}
       </div>

       <div className="report-detail-list">
         {registrosDetalhe.map(l=><button key={l.id} onClick={()=>{setDetalhe(null);navigate(`/lancamentos?acoes=${l.id}`)}}>
           <span><strong>{l.descricao}</strong><small>{dataBR(l.data_realizacao||l.vencimento)} · {l.tipo==='RECEITA'?'Entrada':'Saída'}</small></span>
           <strong className={`${l.tipo==='RECEITA'?'income':'expense'} privacy-value`}>{moeda(valor(l))}</strong>
         </button>)}
         {!registrosDetalhe.length&&<div className="empty-state">Nenhum lançamento realizado no filtro.</div>}
       </div>
     </div>
   </Modal>
 </div>
}

